import React, { useEffect, useRef, useState } from 'react';
import { FolderOpen, FileText, X, Loader2, ZoomIn, ZoomOut } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PageImage = ({ fileId, pageNum, token }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    let objectUrl = null;
    fetch(`${API_URL}/api/dataroom/documents/${fileId}/page/${pageNum}`, {
      headers: { 'X-Dataroom-Token': token },
    })
      .then(r => (r.ok ? r.blob() : Promise.reject(new Error('page failed'))))
      .then(b => {
        objectUrl = URL.createObjectURL(b);
        if (alive) setSrc(objectUrl);
      })
      .catch(() => {});
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [fileId, pageNum, token]);

  if (!src) {
    return (
      <div className="w-full max-w-3xl mx-auto rounded bg-white/10 flex items-center justify-center" style={{ minHeight: 320 }}>
        <Loader2 size={20} className="text-white/50 animate-spin" />
      </div>
    );
  }
  return (
    <img alt={`Page ${pageNum}`} draggable={false} src={src}
      className="w-full mx-auto rounded shadow-lg select-none pointer-events-none" />
  );
};

export const DataRoomDocuments = ({ token }) => {
  const [folders, setFolders] = useState(null);
  const [viewer, setViewer] = useState(null); // { fileId, label, pages }
  const [zoom, setZoom] = useState(1);
  const lastTapRef = useRef(0);
  const [opening, setOpening] = useState(null);
  const [error, setError] = useState('');

  const handleTapZoom = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 350) {
      setZoom(z => (z === 1 ? 2 : 1));
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  };

  useEffect(() => {
    if (!token) return;
    let alive = true;
    fetch(`${API_URL}/api/dataroom/documents`, { headers: { 'X-Dataroom-Token': token } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('load failed'))))
      .then(d => { if (alive) setFolders(d.folders || []); })
      .catch(() => { if (alive) setFolders([]); });
    return () => { alive = false; };
  }, [token]);

  const openDoc = async (file) => {
    setOpening(file.file_id);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/dataroom/documents/${file.file_id}/open`, {
        method: 'POST',
        headers: { 'X-Dataroom-Token': token },
      });
      if (!res.ok) throw new Error('open failed');
      const d = await res.json();
      setZoom(1);
      setViewer({ fileId: file.file_id, label: d.label || file.label, pages: d.pages });
    } catch {
      setError('Could not open this document. Please try again.');
    } finally {
      setOpening(null);
    }
  };

  if (!token) return null;
  if (folders === null) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm" data-testid="dr-documents-loading">
        <p className="text-xs text-gray-400">Loading document library…</p>
      </div>
    );
  }
  if (!folders.length) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm" data-testid="dr-documents">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-black text-[#1A1209] uppercase tracking-wider">Diligence Documents</h3>
        <span className="text-[10px] text-gray-400 font-semibold">View-only · Watermarked</span>
      </div>
      <p className="text-[11px] text-gray-500 mb-4">Click any document to read it inside this secure room. Downloading is disabled.</p>

      <div className="space-y-4">
        {folders.map(f => (
          <div key={f.folder_id} data-testid={`dr-doc-folder-${f.folder_id}`}>
            <div className="flex items-center gap-2 mb-2">
              <FolderOpen size={14} className="text-[#7B4FD8]" />
              <p className="text-xs font-bold text-[#1A1209]">{f.label}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {f.files.length === 0 && (
                <p className="text-[10px] text-gray-400 italic col-span-2" data-testid={`dr-doc-empty-${f.folder_id}`}>Documents for this section are being compiled and will appear here once added.</p>
              )}
              {f.files.map(file => (
                <button key={file.file_id} onClick={() => openDoc(file)} disabled={opening === file.file_id}
                  className="flex items-start gap-2 text-left border border-gray-100 rounded-xl px-3 py-2.5 hover:border-[#7B4FD8]/40 hover:bg-purple-50/40 transition-colors disabled:opacity-60"
                  data-testid={`dr-doc-file-${file.file_id}`}>
                  {opening === file.file_id
                    ? <Loader2 size={14} className="text-[#7B4FD8] mt-0.5 flex-shrink-0 animate-spin" />
                    : <FileText size={14} className="text-[#7B4FD8] mt-0.5 flex-shrink-0" />}
                  <span>
                    <span className="block text-[11px] font-semibold text-gray-800 leading-snug">{file.label}</span>
                    {file.description && <span className="block text-[10px] text-gray-400 mt-0.5 leading-snug">{file.description}</span>}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {error && <p className="text-[11px] text-rose-500 mt-3 font-semibold" data-testid="dr-doc-error">{error}</p>}

      {viewer && (
        <div className="fixed inset-0 z-[10000] bg-black/80 flex flex-col" data-testid="dr-doc-viewer">
          <div className="flex items-center justify-between px-5 py-3 bg-[#1A1209]">
            <p className="text-xs font-bold text-white truncate pr-4">{viewer.label}</p>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="hidden sm:inline text-[10px] text-gray-400">{viewer.pages} page{viewer.pages === 1 ? '' : 's'} · View only</span>
              <button onClick={() => setZoom(z => Math.max(1, z - 0.5))} className="text-white/70 hover:text-white disabled:opacity-30" disabled={zoom <= 1} data-testid="dr-doc-zoom-out" aria-label="Zoom out">
                <ZoomOut size={18} />
              </button>
              <span className="text-[10px] text-gray-400 w-8 text-center">{Math.round(zoom * 100)}%</span>
              <button onClick={() => setZoom(z => Math.min(3, z + 0.5))} className="text-white/70 hover:text-white disabled:opacity-30" disabled={zoom >= 3} data-testid="dr-doc-zoom-in" aria-label="Zoom in">
                <ZoomIn size={18} />
              </button>
              <button onClick={() => setViewer(null)} className="text-white/70 hover:text-white" data-testid="dr-doc-viewer-close">
                <X size={18} />
              </button>
            </div>
          </div>
          <p className="text-center text-[10px] text-gray-500 bg-[#1A1209] pb-2 sm:hidden">Double-tap a page to zoom</p>
          <div className="flex-1 overflow-auto px-2 sm:px-8 py-4" onContextMenu={e => e.preventDefault()}
            onTouchEnd={handleTapZoom} onDoubleClick={() => setZoom(z => (z === 1 ? 2 : 1))}
            style={{ touchAction: 'pan-x pan-y' }}>
            <div className="space-y-3 mx-auto" style={{ width: zoom > 1 ? `${zoom * 100}%` : '100%', maxWidth: zoom > 1 ? 'none' : '48rem' }}>
              {Array.from({ length: viewer.pages }).map((_, i) => (
                <PageImage key={i} fileId={viewer.fileId} pageNum={i + 1} token={token} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataRoomDocuments;
